import DeleteIcon from "@mui/icons-material/DeleteRounded";
import { Button, Stack, Typography } from "@mui/material";
import { FormikTextField } from "@shared";
import { FormikProps } from "formik";

import { useTranslation } from "@/translations";

import { TemplateFormValues, TemplateLabels, TemplateSubjectConfig } from "../TemplateManagement.types";

interface Props {
  hasSelection: boolean;
  isNew: boolean;
  formik: FormikProps<TemplateFormValues>;
  labels: TemplateLabels;
  titleMaxLength: number;
  subject?: TemplateSubjectConfig;
  isSaving: boolean;
  isDeleting: boolean;
  onDelete: () => void;
  onCancel: () => void;
}

export function TemplateEditor({
  hasSelection,
  isNew,
  formik,
  labels,
  titleMaxLength,
  subject,
  isSaving,
  isDeleting,
  onDelete,
  onCancel,
}: Props) {
  const { t } = useTranslation();

  if (!hasSelection) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center">
        <Typography color="text.secondary">{labels.noTemplateSelected}</Typography>
      </Stack>
    );
  }

  return (
    <Stack component="form" onSubmit={formik.handleSubmit} sx={{ flex: 1, maxHeight: "100%", gap: 1 }}>
      <Stack flex={1} gap={1} sx={{ overflowY: "auto" }}>
        <Stack flex={1} gap={2} pt={1}>
          <FormikTextField
            formik={formik}
            fieldName="title"
            label={labels.title}
            required
            slotProps={{ input: { inputProps: { maxLength: titleMaxLength } } }}
          />
          {subject && (
            <FormikTextField
              formik={formik}
              fieldName="subject"
              label={subject.label}
              required
              slotProps={{ input: { inputProps: { maxLength: subject.maxLength } } }}
            />
          )}
          <FormikTextField formik={formik} fieldName="content" label={labels.content} required multiline rows={6} />
          <Typography variant="caption" color="text.secondary">
            {t("settings.templates.placeholderPanel.insertTip")}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="center" p={0.5}>
        {isNew ? (
          <Button variant="outlined" onClick={onCancel} disabled={isSaving}>
            {t("common.cancel")}
          </Button>
        ) : (
          <Stack direction="row" gap={1}>
            <Button variant="outlined" onClick={() => formik.resetForm()} disabled={!formik.dirty || isSaving}>
              {t("common.resetChanges")}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={onDelete}
              disabled={isDeleting}
            >
              {t("common.delete")}
            </Button>
          </Stack>
        )}
        <Button type="submit" variant="contained" disabled={!formik.dirty || isSaving}>
          {t("common.save")}
        </Button>
      </Stack>
    </Stack>
  );
}
